import { useTranslation } from "react-i18next";
import { useMemo, memo } from "react";
import { Button } from "@/components/ui/button";
import { QuestionnaireCheckboxField } from "../shared/QuestionnaireCheckboxField";
import { QuestionnaireFormSection } from "../shared/QuestionnaireFormSection";
import { useQuestionnaireData } from "../../hooks";
import { FilePreviewDialog } from "@/components/file-preview/FilePreviewDialog";
import { useFilePreview } from "@/components/file-preview/hooks/useFilePreview";
import {
  getAttachmentNumber,
  getFileNameFromPath,
  getSortedQuarters,
  getQuarterPeriod,
} from "./utils/regulationsUtils";
import { buildFileUrl } from "@/lib/utils/buildFileUrl";

export const QuestionnaireRegulationsSection = memo(
  function QuestionnaireRegulationsSection() {
    const { t } = useTranslation();
    const { regulations } = useQuestionnaireData();
    const { preview, openPreviewDialog, closePreview } = useFilePreview();

    const sortedQuarters = useMemo(
      () => getSortedQuarters(regulations),
      [regulations],
    );

    const handleOpenRegulation = (filePath: string) => {
      const fileUrl = buildFileUrl(filePath);
      if (fileUrl) {
        const fileName = getFileNameFromPath(filePath);
        openPreviewDialog(fileUrl, fileName);
      }
    };

    if (sortedQuarters.length === 0) {
      return null;
    }

    return (
      <QuestionnaireFormSection>
        <div className="space-y-6">
          {sortedQuarters.map((quarter) => {
            const filePaths = regulations[quarter];
            const period = getQuarterPeriod(quarter);
            return (
              <div key={quarter} className="space-y-4">
                <h4 className="text-base font-semibold text-center">
                  {t(`questionnaires.form.regulations.quarterTitle`, {
                    period,
                  })}
                </h4>
                <div>
                  <div className="mb-2 flex items-start">
                    <span className="mr-2 text-sm">•</span>
                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-800">
                        {t(
                          "questionnaires.form.regulations.acceptanceSection1",
                          {
                            period,
                          },
                        )}
                      </p>
                      <div className="ml-4 space-y-1">
                        {filePaths.map((filePath: string) => {
                          const fileName = getFileNameFromPath(filePath);
                          const attachmentNumber =
                            getAttachmentNumber(fileName);
                          return (
                            <div key={filePath} className="flex items-center">
                              <span className="mr-2 text-sm">•</span>
                              <Button
                                variant="link"
                                className="h-auto p-0 text-sm text-blue-600 underline hover:text-blue-800"
                                onClick={() => handleOpenRegulation(filePath)}
                              >
                                {t(
                                  "questionnaires.form.regulations.attachmentLabel",
                                  {
                                    number: attachmentNumber,
                                  },
                                )}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-sm text-gray-800">
                        {t(
                          "questionnaires.form.regulations.acceptanceSection2",
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start">
                    <span className="mr-2 text-sm">•</span>
                    <div>
                      <p className="mb-2 text-sm font-medium text-gray-800">
                        {t(
                          "questionnaires.form.regulations.dataProcessingSection",
                          {
                            period,
                          },
                        )}
                      </p>
                      <div className="ml-4 space-y-1">
                        {filePaths.map((filePath: string) => {
                          const fileName = getFileNameFromPath(filePath);
                          const attachmentNumber =
                            getAttachmentNumber(fileName);
                          return (
                            <div
                              key={`data-${filePath}`}
                              className="flex items-center"
                            >
                              <span className="mr-2 text-sm">•</span>
                              <Button
                                variant="link"
                                className="h-auto p-0 text-sm text-blue-600 underline hover:text-blue-800"
                                onClick={() => handleOpenRegulation(filePath)}
                              >
                                {t(
                                  "questionnaires.form.regulations.attachmentLabel",
                                  {
                                    number: attachmentNumber,
                                  },
                                )}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-sm text-gray-800">
          {t(
            "questionnaires.form.regulations.nonAgriculturalBusinessStatement",
          )}
        </p>

        <QuestionnaireCheckboxField
          name="regulations_accepted"
          label={t("questionnaires.form.regulations.acceptRegulations")}
          required
          isImportant
        />

        {preview && preview.fileUrl && (
          <FilePreviewDialog
            open
            onClose={closePreview}
            fileUrl={preview.fileUrl}
            fileName={preview.fileName}
          />
        )}
      </QuestionnaireFormSection>
    );
  },
);
